"use client";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import styles from "../css/NewScriptForm.module.css";
import GenericPopup from "../../Dispensary/Script/views/GenericPopup";
import RadioButton from "../../../components/RadioButton";
import MedicalAidModal from "../../Dispensary/Script/views/MedicalAidModal";
import MedicationCard from "../../Dispensary/Script/components/views/MedicationCard";
import PatientPaymentSection from "../../Dispensary/Script/components/views/PatientPaymentSection";
import { useAuth } from "../../../context/AuthContext";
import { fetchNextScriptNumber, fetchProfileInfo, fetchProfileFamily, fetchScriptDetails, fetchScriptDocumentHistory } from "../../../api/dispensary/script/processScriptApi.js";
import useGenericSubstitution from "../../Dispensary/Script/hooks/useGenericSubstitution";
import ErrorModal from "../../../components/ErrorModal";
import Table from "../../../components/Table";
const patientOptions = ["Smith J (P001)", "Johnson A (P002)", "Brown R (P003)"];
export default function ScriptForm({title = "Prescriptions",showMedicationSection = true,showPricingSection = true,showPatient = true, showPractitioner = true,showPharmacist = true,showPaymentType = true,showMedicalAidNumber = true,showChronic = true,showRepeat = true,showScriptNumber = true,skipProfilePractitionerPrefill = false,useDefaultPractitionerOptions = true, initialPatient = "",initialPractitioner = "",initialPharmacist = "", initialPaymentType = "",disablePatient = false, disablePractitioner = false,paymentTypeReadOnly = false,inventoryDepartmentId = null,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    profileNo,
    patientId,
    patientScripts,
    amendFlow,
    scriptNo,
    medications: navMedications,
    prefillMedications,
    patientName: navPatientName,
    initialPatient: navInitialPatient,
    initialPractitioner: navInitialPractitioner,
    practitionerDetails: navPractitionerDetails,
    initialPharmacist: navInitialPharmacist,
    processcriptnumber,
    removedDrugs: navRemovedDrugs,
    originalDrugs: navOriginalDrugs,
    initialScriptType,
    initialIsChronic,
    initialIsRepeat,
    initialRepeatCount,
    paymentType: navPaymentType,
    medicalAidNumber: navMedicalAidNumber,
    // Existing script document URL passed in from amend flows so the
    // payment summary can fall back to the stored document when no new
    // file is uploaded.
    scriptDocUrl: navScriptDocUrl,
  } = location.state || {};
  const isClinicScriptFlow = !!prefillMedications;
  const { user } = useAuth();
  const proceededRef = useRef(false);
  const [patient, setPatient] = useState("");
  const [practitioner, setPractitioner] = useState("");
  const [pharmacist, setPharmacist] = useState(""); // <-- Added pharmacist state
  const [paymentType, setPaymentType] = useState("Cash");
  const [medicalAidNumber, setMedicalAidNumber] = useState("");
  const [isChronic, setIsChronic] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const [repeatCount, setRepeatCount] = useState(0);
  const [medications, setMedications] = useState([]);
  const [scriptType, setScriptType] = useState("PR");
  const [showPopup, setShowPopup] = useState(false); // maintained for backward compatibility but controlled by hook
  const [showMedicalAidModal, setShowMedicalAidModal] = useState(false);
  const [medicalAidActionTaken, setMedicalAidActionTaken] = useState(false);
  const [enteredScriptNo, setEnteredScriptNo] = useState(
    processcriptnumber != null
      ? String(processcriptnumber)
      : scriptNo != null
      ? String(scriptNo)
      : ""
  );
  const [patientOptionsList, setPatientOptionsList] = useState([]);
  const [practitionerOptionsList, setPractitionerOptionsList] = useState([]);
  const [pharmacistOptionsList, setPharmacistOptionsList] = useState([]);
  const [popupData, setPopupData] = useState([]); 
  const [validationMessage, setValidationMessage] = useState("");
  const [practitionerId, setPractitionerId] = useState(null);
  const [practiceNumber, setPracticeNumber] = useState("");
  const [addressLines, setAddressLines] = useState([]);
  const [phone, setPhone] = useState("");
  const [scriptFile, setScriptFile] = useState(null);
  const [documentCount, setDocumentCount] = useState(0);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [documentHistory, setDocumentHistory] = useState([]);
  const scriptIdNumericRef = useRef(null);
  // Fetch next auto-generated script number for new scripts (not amend flows)
  useEffect(() => {
    // Only fetch for new process scripts (not OTC, not amend flows)
    if (amendFlow || title === "OTC Dispense") return;
    
    // Don't fetch if we already have a script number from navigation state
    if (processcriptnumber || scriptNo) return;
    
    let cancelled = false;
    const loadNextScriptNumber = async () => {
      try {
        const nextScriptNo = await fetchNextScriptNumber();
        if (!cancelled && nextScriptNo) {
          setEnteredScriptNo(nextScriptNo);
        }
      } catch (e) {
        console.error("[NewScriptForm] Failed to fetch next script number:", e);
      }
    };
    
    loadNextScriptNumber();
    return () => { cancelled = true; };
  }, [amendFlow, title, processcriptnumber, scriptNo]);
  useEffect(() => {
    proceededRef.current = false;
    const incomingPatientLabel = navInitialPatient || navPatientName || "";
    if (incomingPatientLabel && !patient) {
      setPatient(incomingPatientLabel);
    }
    const navPractitionerProvided = !!(navInitialPractitioner || navPractitionerDetails);
    if (navPractitionerProvided) {
      const p = navPractitionerDetails || null;
      const display = navInitialPractitioner || (() => {
        if (!p) return "";
        const titlePart = p.title ? `${p.title} ` : "";
        const namePart = [p.surname, p.firstname].filter(Boolean).join(" ");
        return `${titlePart}${namePart}`.trim();
      })();
      if (display && !practitioner) {
        setPractitioner(display);
        setPractitionerOptionsList([display]);
      }
      const pn =
        p?.practicenumber ||
        p?.practiceNumber ||
        p?.practiseNumber ||
        "";
      if (pn) {
        setPracticeNumber(pn);
      }
      const pid = p?.practitionerid || p?.practitionerId || null;
      if (pid != null) {
        setPractitionerId(pid);
      }
    }
    if (navInitialPharmacist && !pharmacist) {
      setPharmacist(navInitialPharmacist);
      setPharmacistOptionsList([navInitialPharmacist]);
    }
    if (profileNo) {
      const preSelected = patientOptions.find((option) =>
        option.includes(profileNo)
      );
      if (!patient) setPatient(preSelected || "");
    }
    if (initialPatient) setPatient(initialPatient);
    if (initialPractitioner) {
      setPractitioner(initialPractitioner);
      setPractitionerOptionsList([initialPractitioner]);
    }
    if (initialPharmacist) setPharmacist(initialPharmacist); 
    if (initialPaymentType) setPaymentType(initialPaymentType);
    try {
      const stored = sessionStorage.getItem("currentScript");
      const comingFromOTCSummaryFlag = (() => {
        try { return sessionStorage.getItem("cameFromOTCSummary") === "1"; } catch { return false; }
      })();
      const shouldHydrate = comingFromOTCSummaryFlag || !(
        title === "OTC Dispense" && (initialPatient || initialPractitioner || initialPaymentType)
      );
      const allowNavMedicationPrefill = amendFlow || isClinicScriptFlow;
      if (allowNavMedicationPrefill) {
        if (Array.isArray(navMedications) && navMedications.length > 0) {
          setMedications(navMedications);
        }
        // Keep the original payment type on amend, else a medical-aid rewrite
        // silently becomes Cash.
        if (navPaymentType) {
          setPaymentType(navPaymentType);
        }
        if (navMedicalAidNumber) {
          setMedicalAidNumber(navMedicalAidNumber);
        }
        if (initialScriptType) {
          setScriptType(initialScriptType);
        }
        if (typeof initialIsChronic === "boolean") {
          setIsChronic(initialIsChronic);
        }
        if (typeof initialIsRepeat === "boolean") {
          setIsRepeat(initialIsRepeat);
        }
        if (initialRepeatCount != null) {
          setRepeatCount(initialRepeatCount);
        }
      } else if (stored && shouldHydrate) {
        const s = JSON.parse(stored);
        if (s) {
          console.log("[NewScriptForm] Hydrating from sessionStorage.currentScript", { stored: s });
          setPatient(s.patient || "");
          setPractitioner(s.practitioner || "");
          setPharmacist(s.pharmacist || "");
          setPaymentType(s.paymentType || "Cash");
          setMedicalAidNumber(s.medicalAidNumber || "");
          setIsChronic(!!s.isChronic);
          setIsRepeat(!!s.isRepeat);
          setRepeatCount(s.repeatCount || 0);
          setScriptType(s.scriptType || "PR");
          if (Array.isArray(s.drugs)) setMedications(s.drugs);
          setEnteredScriptNo(
            s.scriptNumber != null
              ? String(s.scriptNumber)
              : s.scriptNo != null
              ? String(s.scriptNo)
              : ""
          );
        }
      } else if (Array.isArray(navMedications) && navMedications.length > 0 && allowNavMedicationPrefill) {
        // As a safety net, avoid pre-populating medications from navMedications
        // when this is a brand new script (non-amend). This prevents cases
        // where selecting a patient/script context would incorrectly bring in
        // drugs from an existing prescription when creating a fresh script.
        setMedications(navMedications);
      }
    } catch {}
    return () => {
      const comingFromPriceSummary = (() => {
        try { return sessionStorage.getItem("cameFromPriceSummary") === "1"; } catch { return false; }
      })();
      const comingFromOTCSummary = (() => {
        try { return sessionStorage.getItem("cameFromOTCSummary") === "1"; } catch { return false; }
      })();
      const path = window.location.pathname || "";
      const isNewScriptRoute = path.includes("/customer/dispensary/new-script");
      const isScriptPaymentSummaryRoute = path.includes("/customer/dispensary/script/payment-summary");
      if (!proceededRef.current && !comingFromPriceSummary && !comingFromOTCSummary && !isNewScriptRoute && !isScriptPaymentSummaryRoute) {
        try {
          console.log("[NewScriptForm] Clearing session draft on unmount", { path });
          sessionStorage.removeItem("currentScript");
          sessionStorage.removeItem("cameFromPriceSummary");
          sessionStorage.removeItem("cameFromOTCSummary");
          sessionStorage.removeItem("priceScriptActive");
        } catch {}
      }
    };
  }, [patientScripts, profileNo, initialPatient, initialPractitioner, initialPharmacist, initialPaymentType, title]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!profileNo) return;
      try {
        const [info, family] = await Promise.all([
          fetchProfileInfo(profileNo),
          fetchProfileFamily(profileNo),
        ]);
        if (cancelled) return;
        const familyOptions = (family || []).map((f) => ({ value: String(f.id), label: f.label }));
        setPatientOptionsList(familyOptions.map((o) => o.label));
        if (patientId) {
          const selected = familyOptions.find((o) => o.value === String(patientId));
          console.log("[NewScriptForm] Prefill/override patient from profile", {
            profileNo,
            patientId,
            previousPatientLabel: patient,
            selected,
          });
          if (selected) setPatient(selected.label);
        }

        console.log("[NewScriptForm] Profile info loaded", { profileNo, patientId, info, family });
        const navPractitionerProvided = !!(navInitialPractitioner || navPractitionerDetails);
        if (navPractitionerProvided) {
          return;
        }
        const resolvedPractitionerId =
          (info && (info.practitionerid || info.practitionerId)) ||
          info?.practitioner?.practitionerid ||
          info?.practitioner?.practitionerId ||
          null;
        if (resolvedPractitionerId != null) {
          setPractitionerId(resolvedPractitionerId);
        }
        const p = info?.practitioner || info?.mainPatient?.practitioner || null;
        if (p) {
          const titlePart = p.title ? `${p.title} ` : "";
          const namePart = [p.surname, p.firstname].filter(Boolean).join(" ");
          const display = `${titlePart}${namePart}`.trim();
          if (display) {
            setPractitioner(display);
            setPractitionerOptionsList([display]);
          }
          if (p.practicenumber) {
            setPracticeNumber(p.practicenumber);
          }
        }
        if (info?.medicalAidNumber) setMedicalAidNumber(info.medicalAidNumber);

        const main = info?.mainPatient || info?.mainMember || info?.patient || null;
        if (main) {
          const addr = [];
          if (main.address1) addr.push(main.address1);
          if (main.address2) addr.push(main.address2);
          if (main.suburb) addr.push(main.suburb);
          if (main.city) addr.push(main.city);
          if (main.postalCode) addr.push(main.postalCode);
          setAddressLines(addr);

          const phoneValue =
            main.cellphone ||
            main.cellphoneNo ||
            main.cell ||
            main.mobile ||
            main.phone ||
            main.telephone ||
            "";
          setPhone(phoneValue);
        }
      } catch (e) {
      }
    };
    load();
    return () => { cancelled = true; };
  }, [profileNo, patientId, patient, practitioner]);
  useEffect(() => {
    const fullName = [user?.name || user?.firstname, user?.surname].filter(Boolean).join(" ");
    if (fullName && !navInitialPharmacist) {
      setPharmacist(fullName);
      setPharmacistOptionsList([fullName]);
    }
  }, [user]);
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname || "";
      if (path.includes("/customer/dispensary/new-script") || path.includes("/customer/dispensary/price-script") || path.includes("/customer/dispensary/otc-direct/medication")) {
        const fromPrice = (() => { try { return sessionStorage.getItem("cameFromPriceSummary") === "1"; } catch { return false; } })();
        const fromOTC = (() => { try { return sessionStorage.getItem("cameFromOTCSummary") === "1"; } catch { return false; } })();
        if (!fromPrice && !fromOTC) {
          try {
            sessionStorage.removeItem("currentScript");
            sessionStorage.removeItem("cameFromPriceSummary");
            sessionStorage.removeItem("cameFromOTCSummary");
            sessionStorage.removeItem("priceScriptActive");
          } catch {}
        }
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Resolve numeric script ID for amend flows and fetch document count/history metadata
  useEffect(() => {
    if (!amendFlow) return;
    console.log("[new-script-form] amendFlow detected. scriptNo/processcriptnumber/enteredScriptNo:", { scriptNo, processcriptnumber, enteredScriptNo });
    const firstNumeric = (v) => {
      if (v == null) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const idNumeric = firstNumeric(scriptNo)
      ?? firstNumeric(processcriptnumber)
      ?? firstNumeric(enteredScriptNo);
    console.log("[new-script-form] resolved idNumeric:", idNumeric);
    if (!idNumeric) return;
    scriptIdNumericRef.current = idNumeric;
    let alive = true;
    (async () => {
      try {
        const data = await fetchScriptDetails(String(idNumeric));
        if (!alive || !data) return;
        const count = Number(data.script?.documentCount || 0);
        console.log("[new-script-form] fetched script details; documentCount:", count);
        setDocumentCount(count);
      } catch (e) {
        console.error("Failed to fetch script details for document count", e);
      }
    })();
    return () => { alive = false; };
  }, [amendFlow, scriptNo, processcriptnumber, enteredScriptNo]);
  const handleShowDocumentHistory = async () => {
    const idNumeric = scriptIdNumericRef.current;
    if (!idNumeric) return;
    setHistoryError("");
    setHistoryLoading(true);
    try {
      const docs = await fetchScriptDocumentHistory(String(idNumeric));
      const normalized = Array.isArray(docs) ? docs : [];
      console.log("[new-script-form] fetched documentHistory:", normalized);
      setDocumentHistory(normalized);
      setHistoryModalOpen(true);
    } catch (e) {
      console.error("Failed to fetch script document history", e);
      setHistoryError("Failed to load document history.");
    } finally {
      setHistoryLoading(false);
    }
  };
  const handleAddMedication = () => {
    if (medications.length > 0 && medications[medications.length - 1]?.saved === false) {
      setValidationMessage("Please save the previous medication before adding another.");
      return;
    }
    setMedications([
      ...medications,
      {
        id: medications.length + 1, drugName: "", strength: "", quantity: "1", packSize: "", icd10: "", nappiCode: "", instructions: "", costPrice: "", deliveryOption: "Collect", saved: false,
      },
    ]);
  };
  const handleMedicationChange = (id, field, value) => {
    setMedications((prev) =>
      prev.map((med) => (med.id === id ? { ...med, [field]: value } : med))
    );
  };
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0] ?? null;
    if (file) {
      setScriptFile(file);
      console.log("Selected script file:", file.name);
    } else {
      setScriptFile(null);
    }
  };
  const generic = useGenericSubstitution({ medications, setMedications });
  const handleSaveMedication = (id) => {
    const med = medications.find((m) => m.id === id);
    if (!med) return;
    if (med.saved) {
      return;
    }
    const hasDirections = !!(med.instructions && (med.instructions !== "Other" || (med.instructionsOther && String(med.instructionsOther).trim() !== "")));
    if (!hasDirections) {
      setValidationMessage("Please select directions for this medication.");
      return;
    }
    generic.openForMedication(id);
  };
  const handleGenericClose = () => {
    const id = generic.savingMedId;
    if (id != null) {
      setMedications((prev) => prev.map((m) => (m.id === id ? { ...m, saved: true } : m)));
    }
    generic.closePopup();
  };
  const handleGenericReplace = async (row) => {
    const id = generic.savingMedId;
    await generic.replaceWithSku(row);
    if (id != null) {
      setMedications((prev) => prev.map((m) => (m.id === id ? { ...m, saved: true } : m)));
    }
  };
  const buildScriptData = () => {
    const derivedScriptNo = scriptNo
      || (Array.isArray(patientScripts) && patientScripts.length > 0
          ? `${patientScripts[0]?.prescriptionId || patientScripts[0]?.id || "SCR"}` // eslint-disable-line no-nested-ternary
          : `SCR_${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`);
    const enteredScriptNoStr =
      typeof enteredScriptNo === "string"
        ? enteredScriptNo
        : enteredScriptNo == null
        ? ""
        : String(enteredScriptNo);
    const finalScriptNo = enteredScriptNoStr.trim() || derivedScriptNo;
    const scriptData = {
      drugs: medications,
      patient,
      practitioner,
      pharmacist,
      paymentType,
      medicalAidNumber,
      isChronic,
      isRepeat,
      repeatCount,
      scriptType,
      scriptNumber: finalScriptNo,
      scriptNo: finalScriptNo,
      profileNo: profileNo || null,
      patientId: patientId || null,
      practitionerId: practitionerId || null,
      practiceNumber: practiceNumber || undefined,
      branchcode: user?.branchcode || null,
      totalItems: Array.isArray(medications)
        ? medications.reduce((sum, m) => sum + (parseFloat(m.quantity) || 0), 0)
        : 0,
      profileNo: profileNo || undefined,
      patientId: patientId || undefined,
      addressLines,
      phone,
      amendFlow: !!amendFlow,
      removedDrugs: Array.isArray(navRemovedDrugs) ? navRemovedDrugs : [],
      originalDrugs: Array.isArray(navOriginalDrugs) ? navOriginalDrugs : undefined,
      // For amend flows, keep track of the original prescription ID so the
      // backend can safely update the correct record instead of trying to
      // resolve by script number.
      originalPrescriptionId: amendFlow ? (scriptNo || null) : undefined,
      // Carry through any existing script document URL from amend flows so
      // payment summary can fall back to it when no new file is uploaded.
      scriptDocUrl: navScriptDocUrl || undefined,
    };
    try { sessionStorage.setItem("currentScript", JSON.stringify(scriptData)); } catch {}
    return scriptData;
  };
  const navigateToPriceSummary = () => {
    try {
      sessionStorage.setItem("priceScriptActive", "1");
      sessionStorage.setItem("cameFromPriceSummary", "1");
    } catch {}
    const scriptData = buildScriptData();
    navigate("/customer/dispensary/price-script-summary", { state: { scriptData, scriptFile } });
  };
  const navigateToSummary = () => {
    const scriptData = buildScriptData();
    if (title === "OTC Dispense") {
      try { sessionStorage.setItem("cameFromOTCSummary", "1"); } catch {}
      navigate("/customer/dispensary/otc-direct/payment-summary", { state: { scriptData } });
    } else if (title === "Price Script") {
      try {
        sessionStorage.setItem("priceScriptActive", "1");
        sessionStorage.setItem("cameFromPriceSummary", "1");
      } catch {}
      navigate("/customer/dispensary/price-script-summary", { state: { scriptData, scriptFile } });
    } else {
      proceededRef.current = true;
      const targetPath = amendFlow
        ? "/customer/dispensary/amend-script/payment-summary"
        : "/customer/dispensary/script/payment-summary";
      navigate(targetPath, { state: { scriptData, scriptFile } });
    }
  };
  const navigateToClaimStatus = () => {
    const enteredScriptNoStr =
      typeof enteredScriptNo === "string"
        ? enteredScriptNo
        : enteredScriptNo == null
        ? ""
        : String(enteredScriptNo);
    const scriptData = {
      drugs: medications.map(med => ({  ...med,  drugName: med.drugName || "N/A",  packSize: med.packSize || "N/A",  icd10: med.icd10 || "N/A",  strength: med.strength || "N/A",  quantity: med.quantity || "N/A",  costPrice: med.costPrice ? `R${med.costPrice}` : "R0.00",  status: "Acute",
      })),
      patient,practitioner, pharmacist, paymentType, medicalAidNumber, isChronic, isRepeat, repeatCount, scriptType, scriptNumber: enteredScriptNoStr.trim() || scriptNo || undefined,  scriptNo: enteredScriptNoStr.trim() || scriptNo || undefined,  totalItems: Array.isArray(medications) ? medications.length : 0,  profileNo: profileNo || undefined,  patientId: patientId || undefined,  addressLines,  phone,
    };
    try { sessionStorage.setItem("currentScript", JSON.stringify(scriptData)); } catch {}
    const totalCost = medications.reduce((sum, med) => sum + (parseFloat(med.costPrice) || 0), 0);
    const claimDetails = {
      title: "Claim Status",
      claimId: `CLM_${new Date().getFullYear()}-${Math.floor(Math.random() * 10000).toString().padStart(6, '0')}`,
      submitted: new Date().toLocaleString('en-ZA', { 
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).replace(/(\d+)\/(\d+)\/(\d+),/, '$3/$1/$2 '),
      priceTitle: "Price",
      price: `R${totalCost.toFixed(2)}`,
      pricePlaceholder: "R0.00",
      feeTitle: "Professional Fee",
      fee: "R37.67",
      coPayTitle: "Co-Payment",
      coPay: "R14.06",
      coPayPlaceholder: "R0.00",
      grossTitle: "Gross",
      gross: `R${(totalCost + 37.67 + 14.06).toFixed(2)}`,
      progressTitle: "Processing Progress",
      progress: "5",
    };
    navigate("/customer/dispensary/script/claim-status", { state: { scriptData, claimDetails } });
  };

  const handleProceed = () => {
    const hasMeds = Array.isArray(medications) && medications.length > 0;
    if (!hasMeds) {
      setValidationMessage("Please add at least one medication before proceeding.");
      return;
    }
    const hasUnsaved = medications.some((m) => m?.saved === false);
    const hasNoDrug = medications.some((m) => !m?.drugName);
    const hasNoDirections = medications.some((m) => !m?.instructions || (m?.instructions === "Other" && !String(m?.instructionsOther || "").trim()));
    if (hasUnsaved || hasNoDrug || hasNoDirections) {
      setValidationMessage("Please complete and save all medication cards before proceeding.");
      return;
    }
    if (showScriptNumber && !amendFlow && !String(enteredScriptNo || "").trim()) {
      // This validation is now optional since script numbers are auto-generated
      // Keeping the check as a fallback in case auto-generation fails
      console.warn("[NewScriptForm] No script number available, but allowing proceed since backend will auto-generate");
    }
    // For non-OTC *new* scripts, enforce that a script document has been selected.
    // For amend flows we assume an existing document is already attached to the
    // prescription record and do not force a re-upload.
    if (title !== "OTC Dispense" && !amendFlow && !isClinicScriptFlow && !scriptFile) {
      setValidationMessage("Please upload the script document before proceeding.");
      return;
    }
    if (paymentType === "Medical Aid" && !medicalAidActionTaken) {
      setShowMedicalAidModal(true);
    } else {
      // Medical-aid scripts go through the normal payment summary, where the REAL
      // MediKredit claim is submitted after signing (was: navigateToClaimStatus,
      // the old mock claim-status screen).
      navigateToSummary();
    }
  };
  const handleMedicalAidAction = (action) => {
    setShowMedicalAidModal(false);
    setMedicalAidActionTaken(true);
    if (action === "ignore" || action === "proceed") {
      // Real claim happens in the payment-summary flow now, not the mock claim-status.
      navigateToSummary();
    }
  };

  const scriptTypeOptions = [
    { label: "PR", value: "PR" },
    { label: "PR26", value: "PR26" },
    { label: "PRT", value: "PRT" },
    ...(title === "OTC Dispense" ? [{ label: "OTC Price", value: "OTC" }] : []),
  ];

  const handleBackFromForm = () => {
    proceededRef.current = false;
    try {
      sessionStorage.removeItem("currentScript");
      sessionStorage.removeItem("cameFromPriceSummary");
      sessionStorage.removeItem("cameFromOTCSummary");
      sessionStorage.removeItem("priceScriptActive");
    } catch {}
    window.history.back();
  };

  return (
    <div className={styles.container}>
      <Navbar onBack={handleBackFromForm} />
      <main className={styles.main}>
        <div className={styles.contentWrap}>
          <h1 className={styles.title}>{title}</h1>
          <h2 className={styles.sectionHeader}>Prescription Details</h2>
          <PatientPaymentSection
            patient={patient} setPatient={setPatient}
            practitioner={practitioner} setPractitioner={setPractitioner}
            pharmacist={pharmacist} setPharmacist={setPharmacist} 
            paymentType={paymentType} setPaymentType={setPaymentType}
            scriptType={scriptType} setScriptType={setScriptType}
            scriptTypeOptions={scriptTypeOptions}
            medicalAidNumber={medicalAidNumber} setMedicalAidNumber={setMedicalAidNumber}
            isChronic={isChronic} setIsChronic={setIsChronic}
            isRepeat={isRepeat} setIsRepeat={setIsRepeat}
            repeatCount={repeatCount} setRepeatCount={setRepeatCount}
            onFileUpload={handleFileUpload}
            showPatient={showPatient}
            showPractitioner={showPractitioner}
            showPharmacist={showPharmacist}
            showPaymentType={showPaymentType}
            showMedicalAidNumber={showMedicalAidNumber}
            showChronic={showChronic}
            showRepeat={showRepeat}
            showScriptNumber={showScriptNumber}
            disablePatient={disablePatient || !!patientId}
            disablePractitioner={disablePractitioner || (!!profileNo && !skipProfilePractitionerPrefill)}
            disablePharmacist={true}
            patientOptionsList={patientOptionsList}
            practitionerOptionsList={practitionerOptionsList}
            pharmacistOptionsList={pharmacistOptionsList}
            useDefaultPractitionerOptions={useDefaultPractitionerOptions}
            scriptNumber={enteredScriptNo}
            setScriptNumber={undefined}
            scriptNumberReadOnly={true}
            paymentTypeReadOnly={paymentTypeReadOnly}
            documentCount={amendFlow ? documentCount : 0}
            historyLoading={historyLoading}
            historyError={historyError}
            onShowDocumentHistory={handleShowDocumentHistory}
          />
          {showMedicationSection && (
            <>
              <div className={styles.sectionRow}>
                <h2 className={styles.sectionHeader}>Medication</h2>
                <button className={styles.addButton} onClick={handleAddMedication} disabled={medications.length > 0 && medications[medications.length - 1]?.saved === false}>+ Add</button>
              </div>
              {medications.length > 0 && (
                <div className={styles.medicationGrid}>
                  {medications.map((med, idx) => (
                    <MedicationCard
                      key={med.id}
                      med={med}
                      index={idx}
                      inventoryDepartmentId={inventoryDepartmentId}
                      onChange={handleMedicationChange}
                      onDelete={(id) => setMedications(medications.filter((m) => m.id !== id))}
                      onSave={handleSaveMedication}
                    />
                  ))}
                </div>
              )}
            </>
          )}
          {showPricingSection && paymentType !== "Medical Aid" && paymentType !== "Cash" && (
            <>
              <h2 className={styles.sectionHeader}>Pricing</h2>
              <div className={styles.cardbottom}>
                <RadioButton
                  name="scriptType"
                  options={scriptTypeOptions}
                  selectedValue={scriptType}
                  onChange={(value) => setScriptType(value)}
                />
              </div>
            </>
          )}
          <div className={styles.centerProceed}>
            <button
              className={styles.proceedButton}
              onClick={handleProceed}
              disabled={
                medications.length === 0 ||
                medications.some((m) => m?.saved === false || !m?.drugName || !m?.instructions || (m?.instructions === "Other" && !String(m?.instructionsOther || "").trim()))
              }
            >
              Proceed
            </button>
          </div>
          {generic.showPopup && (
            <GenericPopup
              onClose={handleGenericClose}
              title="Generic for selected item is available."
              columns={[
                { header: "Drug", accessor: "drugName" },
                { header: "Cost Price", accessor: "costPrice" },
                {
                  header: "Action",
                  accessor: "action",
                  render: (value, row) => (
                    <button
                      className={generic.replaceButtonClass}
                      onClick={() => handleGenericReplace(row)}
                    >
                      Replace
                    </button>
                  ),
                },
              ]}
              data={generic.popupData}
            />
          )}
          <ErrorModal isOpen={!!validationMessage} onClose={() => setValidationMessage("")} message={validationMessage} />
          {showMedicalAidModal && (
            <MedicalAidModal
              onClose={() => setShowMedicalAidModal(false)}
              onAction={handleMedicalAidAction}
            />
          )}
          {historyModalOpen && (
            <div className={styles.historyModalOverlay}>
              <div className={styles.historyModalContent}>
                <h3>Script Document History</h3>
                <button
                  type="button"
                  onClick={() => setHistoryModalOpen(false)}
                  className={styles.historyModalCloseButton}
                >
                  ×
                </button>
                {documentHistory.length === 0 ? (
                  <p>No documents found for this prescription.</p>
                ) : (
                  <div className={styles.historyTableWrapper}>
                    <table className={styles.historyTable}>
                      <thead>
                        <tr className={styles.historyHeaderRow}>
                          <th className={styles.historyHeaderCell}>Uploaded At</th>
                          <th className={styles.historyHeaderCell}>Uploaded By</th>
                          <th className={styles.historyHeaderCell}>Context</th>
                          <th className={styles.historyHeaderCell}>Active</th>
                          <th className={styles.historyHeaderCell}>File Name</th>
                          <th className={styles.historyHeaderCell}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documentHistory.map((d, idx) => (
                          <tr key={d.id || idx} className={styles.historyRow}>
                            <td className={styles.historyCell}>{d.uploadedAt ? new Date(d.uploadedAt).toLocaleString() : ""}</td>
                            <td className={styles.historyCell}>{d.uploadedBy || ""}</td>
                            <td className={styles.historyCell}>
                              <span className={d.context === "amend" ? styles.historyContextBadgeAmend : styles.historyContextBadge}>
                                {d.context || ""}
                              </span>
                            </td>
                            <td className={styles.historyCell}>
                              <span className={d.isActive ? styles.historyActiveBadge : styles.historyActiveBadgeInactive}>
                                {d.isActive ? "Yes" : "No"}
                              </span>
                            </td>
                            <td className={styles.historyCell}>{d.originalName || ""}</td>
                            <td className={styles.historyCell}>
                              <button
                                type="button"
                                onClick={() => {
                                  if (d.viewUrl && typeof window !== "undefined") {
                                    window.open(d.viewUrl, "_blank", "noopener,noreferrer");
                                  }
                                }}
                                className={styles.historyViewButton}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
